import * as React from 'react';
import {
  Button,
  HStack,
  ListBox,
  Spacer,
  Text,
  Textarea,
  View,
  VStack,
  useListBoxItem,
  ButtonProps,
  ArrowRightIcon,
  mergeRefs,
} from '@packages/systems/spring';
import {MAX_USER_MESSAGE_LENGTH} from '@packages/domains/ai-assistant/data-types';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import type {QuestionOption} from '../../../selectors';
import {prepareQuestionOptions} from './askUserQuestionOptions';
import {
  ASK_USER_QUESTION_KEY_BIND_LABEL_MAP,
  useAskUserQuestionKeyBinds,
} from './useAskUserQuestionKeyBinds';
import {emotionStyled} from '@packages/utilities/styled';

export type AskUserQuestionCardProps = {
  isCompact?: boolean;
  /**
   * True when the AIA panel is docked (left/right), not floating. Combined with
   * isCompact + AIA_PANEL_RELOCATION for the light-theme secondary fill.
   */
  isDocked?: boolean;
  question: string;
  options: QuestionOption[];
  /**
   * Stable id for the pending tool call. Used to reset Other textarea state
   * when a new question arrives.
   */
  resetKey?: string;
  onSelect: (option: QuestionOption) => void;
  onSelectFreeForm: (text: string) => void;
  onCancel: () => void;
};

const QuestionButton = emotionStyled(Button)({
  alignItems: 'flex-start',
  backgroundColor: 'transparent',
  background: 'transparent',
  boxShadow: 'none',
  padding: '$2',
  '.branch-indent-icon': {
    flex: 1,
    justifyItems: 'flex-end',
    opacity: 0,
    marginBottom: '-$1',
  },
  '&:hover, &:focus': {
    outline: 'none',
    background: 'var(--colors-action-secondary-background-hover)',
  },
  '&:hover .branch-indent-icon, &:focus .branch-indent-icon': {
    opacity: 1,
  },
  '> span': {
    textAlign: 'left',
    justifyContent: 'flex-start',
  },
});

const QuestionList = emotionStyled(ListBox)({
  // While an option holds DOM focus (A–E or Tab), suppress hover chrome on
  // siblings so only `:focus` reads as the active highlight.
  '&:focus-within': {
    '[data-component=QuestionButton]:not(:focus):hover': {
      background: 'transparent',
      '[data-sc="SpaceSaver"] > .branch-indent-icon': {
        opacity: 0,
      },
    },
  },
});

const KeyBindLabel = emotionStyled(View)({
  border: '$borderDefault',
  borderRadius: '$2',
  padding: '$1 $2',
  marginRight: '$1',
});

const QuestionListBoxItem = ({
  value,
  isDisabled,
  description,
  children,
  keyBind,
  optionRef,
  ...otherProps
}: {
  keyBind?: number;
  value: string;
  isDisabled?: boolean;
  description?: string;
  children: React.ReactNode;
  optionRef?: (element: HTMLElement | null) => void;
}) => {
  const {
    onClick,
    ref: listBoxRef,
    ...listBoxProps
  } = useListBoxItem({
    isDisabled,
    value,
  });

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.code === 'Space' || event.code === 'Enter') {
      event.preventDefault();
      onClick(event as unknown as React.MouseEvent<HTMLElement, MouseEvent>);
    }
  };

  const keyBindLabel =
    keyBind !== undefined
      ? ASK_USER_QUESTION_KEY_BIND_LABEL_MAP[
          keyBind as keyof typeof ASK_USER_QUESTION_KEY_BIND_LABEL_MAP
        ]
      : undefined;

  return (
    <QuestionButton
      {...(listBoxProps as unknown as ButtonProps)}
      {...otherProps}
      ref={mergeRefs([listBoxRef, optionRef])}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      suffix={
        <div className="branch-indent-icon">
          <ArrowRightIcon />
        </div>
      }
      data-component="QuestionButton"
    >
      <HStack align="stretch" maxWidth="100%">
        {keyBindLabel && (
          <VStack flex={1} placeSelf="flex-start">
            <KeyBindLabel>{keyBindLabel}</KeyBindLabel>
          </VStack>
        )}
        <VStack spacing={0.5} justify="center">
          <Text
            textAlign="left"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
            truncateToLines={2}
            truncationTooltipProps={{label: children}}
          >
            {children}
          </Text>
          {description && (
            <Text
              textAlign="left"
              truncateToLines={2}
              truncationTooltipProps={{label: description}}
              whiteSpace="pre-wrap"
              wordBreak="break-word"
              as="span"
              font="helpText"
              colorVariant="secondary"
            >
              {description}
            </Text>
          )}
        </VStack>
      </HStack>
    </QuestionButton>
  );
};

const FreeFormTextarea = emotionStyled(Textarea)({
  '& textarea': {
    maxHeight: '$8',
    overflowY: 'auto',
  },
});

/**
 * Presentational ask-user-question card. Hosts (chat sidebar / fullscreen
 * AICC clarification) own Flux wiring and pass callbacks.
 */
export const AskUserQuestionCard: React.FC<AskUserQuestionCardProps> = ({
  isCompact,
  isDocked,
  question,
  options,
  resetKey,
  onSelect,
  onSelectFreeForm,
  onCancel,
}) => {
  const [otherText, setOtherText] = React.useState('');
  const [isFreeFormFocused, setIsFreeFormFocused] = React.useState(false);
  const optionElementsRef = React.useRef<Array<HTMLElement | null>>([]);
  const isAiaPanelRelocationEnabled = useFeatureFlag(
    FEATURE_FLAGS.AIA_PANEL_RELOCATION
  );

  const {displayOptions, allowFreeForm} = prepareQuestionOptions(options);

  React.useEffect(() => {
    setOtherText('');
    setIsFreeFormFocused(false);
  }, [resetKey]);

  // A–E keybinds while a question is pending; disabled when the free-form
  // textarea is focused so typing does not select options.
  useAskUserQuestionKeyBinds({
    options: displayOptions,
    onSelect,
    enabled: !isFreeFormFocused,
    resetKey,
    optionElementsRef,
  });

  const setOptionElementRef = React.useCallback(
    (index: number) => (element: HTMLElement | null) => {
      optionElementsRef.current[index] = element;
    },
    []
  );

  const submitFreeForm = () => {
    const trimmed = otherText.trim();
    if (trimmed.length === 0 || trimmed.length > MAX_USER_MESSAGE_LENGTH) {
      return;
    }
    onSelectFreeForm(trimmed);
  };

  const mainWrapperStyle = {
    padding: '$3 $1 0 $1',
    border: '$borderDefault',
    borderRadius: '$3',
    // Secondary fill + scrollbar-gutter inset only for relocated light docked
    // sidebar — dark themes, floating panel, pre-relocation layout, and
    // fullscreen clarification keep the default background and flush edges.
    ...(isCompact && isDocked && isAiaPanelRelocationEnabled
      ? {
          _light: {backgroundColor: '$backgroundSecondary'},
          marginRight: '$3',
        }
      : {}),
  };

  const handleTextareaKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      submitFreeForm();
    }
  };

  return (
    <View {...mainWrapperStyle}>
      <VStack paddingBottom="$3" borderRadius="$3" spacing={isCompact ? 2 : 3}>
        <Text
          font="heading"
          fontWeight={600}
          colorVariant="primary"
          paddingLeft="$2"
          paddingTop="$2"
        >
          {question}
        </Text>
        <VStack spacing={2}>
          <QuestionList
            aria-label={question}
            selectionMode="single"
            spacing={0}
            onChange={(value) => {
              // ListBox passes the item `value` string (we set it to option.label).
              // Resolve back to the full QuestionOption so callers get `.label`.
              const selected = displayOptions.find(
                (option) => option.label === value
              );
              if (selected) onSelect(selected);
            }}
          >
            {displayOptions.map((option, index) => (
              <QuestionListBoxItem
                key={option.label}
                keyBind={index}
                value={option.label}
                description={option.description}
                optionRef={setOptionElementRef(index)}
                data-automation-id="ai-assistant-ask-user-question-option"
              >
                {option.label}
              </QuestionListBoxItem>
            ))}
          </QuestionList>
          {allowFreeForm ? (
            <HStack
              spacing={2}
              padding="$1 $2"
              align="stretch"
              backgroundColor={
                isFreeFormFocused
                  ? '$actionWhiteOutlineBackgroundSelected'
                  : 'transparent'
              }
              borderRadius="$2"
            >
              <FreeFormTextarea
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                onFocus={() => setIsFreeFormFocused(true)}
                onBlur={() => setIsFreeFormFocused(false)}
                onKeyDown={handleTextareaKeyDown}
                placeholder="Type a more specific prompt"
                rows={1}
                maxLength={MAX_USER_MESSAGE_LENGTH}
                data-automation-id="other-textarea"
              />
              {otherText ? (
                <Button
                  onClick={submitFreeForm}
                  colorVariant="primary"
                  density="comfortable"
                  data-automation-id="other-submit"
                  aria-label="Submit specific prompt"
                >
                  <ArrowRightIcon />
                </Button>
              ) : (
                <Button
                  onClick={onCancel}
                  data-automation-id="ask-user-question-skip"
                >
                  Skip
                </Button>
              )}
            </HStack>
          ) : (
            // Yes/No (and other non-free-form) questions still need a dismiss
            // path — otherwise the user is forced to pick an option and cannot
            // answer the pending tool with USER_CANCELED.
            <HStack padding="$2">
              <Spacer />
              <Button
                onClick={onCancel}
                data-automation-id="ask-user-question-skip"
              >
                Skip
              </Button>
            </HStack>
          )}
        </VStack>
      </VStack>
    </View>
  );
};
